import { Search } from "@mui/icons-material"
import { Box, InputAdornment, TextField, Typography } from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

/**
 * Flatten an object to dotted paths, so a payload of unknown shape renders as a
 * flat key/value list instead of nested tables.
 *
 * Arrays keep their index (`links[0].rel`) because the position is meaningful
 * when comparing two payloads that should agree.
 */
export const flattenPayload = (
  value: unknown,
  prefix = "",
  out: [string, unknown][] = [],
): [string, unknown][] => {
  if (value === null || value === undefined) {
    if (prefix) out.push([prefix, value])
    return out
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      if (prefix) out.push([prefix, "[]"])
      return out
    }
    value.forEach((v, i) => flattenPayload(v, `${prefix}[${i}]`, out))
    return out
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) {
      if (prefix) out.push([prefix, "{}"])
      return out
    }
    entries.forEach(([k, v]) => flattenPayload(v, prefix ? `${prefix}.${k}` : k, out))
    return out
  }
  out.push([prefix, value])
  return out
}

export const formatPayloadValue = (v: unknown): string => {
  if (v === null) return "null"
  if (v === undefined) return "—"
  if (typeof v === "boolean") return v ? "true" : "false"
  if (v === "") return "—"
  return String(v)
}

/**
 * Filter on BOTH sides of the pair.
 *
 * Searching keys alone is the obvious implementation and the wrong one here:
 * half the reason to open this drawer is to find which attribute holds a known
 * id ("where does `sst2-res-door-1` appear?"), and a key-only filter answers
 * that with an empty list.
 */
export const filterPayloadRows = (
  rows: [string, unknown][],
  query: string,
): [string, unknown][] => {
  const q = query.trim().toLowerCase()
  if (!q) return rows
  return rows.filter(
    ([k, v]) =>
      k.toLowerCase().includes(q) || formatPayloadValue(v).toLowerCase().includes(q),
  )
}

/**
 * A payload rendered as a flat, searchable key/value list.
 *
 * Deliberately NOT `ObjectDisplayTable`: that brings a header row, per-row
 * rules and a pagination footer, and three of them side by side turned the
 * drawer into a wall of borders with the values squeezed out of view. This
 * keeps the one control that earns its space — a local attribute search — and
 * drops the rest of the chrome.
 */
export const PayloadList: FC<{ data: unknown; emptyLabel?: string }> = ({
  data,
  emptyLabel,
}) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")

  const rows = useMemo(() => flattenPayload(data), [data])
  const shown = useMemo(() => filterPayloadRows(rows, query), [rows, query])

  if (!rows.length) {
    return (
      <Typography variant="caption" color="text.disabled">
        {emptyLabel || "—"}
      </Typography>
    )
  }

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("saLockerConsole.searchAttribute")}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1.5,
          "& .MuiInputBase-input": { fontSize: "0.75rem", py: 0.75 },
        }}
      />

      {shown.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          {t("saLockerConsole.noAttributeMatch", { query: query.trim() })}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(90px, 38%) 1fr",
            columnGap: 1.5,
            rowGap: 0.25,
            alignItems: "baseline",
          }}
        >
          {shown.map(([k, v]) => (
            <Box key={k} sx={{ display: "contents" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ overflowWrap: "anywhere", lineHeight: 1.6 }}
              >
                {k}
              </Typography>
              <Typography
                variant="caption"
                fontFamily="monospace"
                sx={{
                  overflowWrap: "anywhere",
                  lineHeight: 1.6,
                  color:
                    v === null || v === undefined || v === "" ? "text.disabled" : "text.primary",
                }}
              >
                {formatPayloadValue(v)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default PayloadList
